import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { TrendingUp, TrendingDown, Trash2, Scale } from 'lucide-react';
import { toast } from 'react-toastify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WeightTracker = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [chartData, setChartData] = useState([]);
  const [entries, setEntries] = useState([]);
  
  // Form state - use local date to avoid timezone issues
  const getLocalDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [date, setDate] = useState(getLocalDate());
  const [weight, setWeight] = useState('');
  const [unit, setUnit] = useState('lbs');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (user?.id) {
      fetchWeightHistory();
    }
  }, [user?.id, selectedPeriod]);

  const fetchWeightHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users/${user.id}/weight-tracker/history/${selectedPeriod}`);
      
      if (response.data.success && response.data.data.length > 0) {
        const transformed = response.data.data.map(entry => {
          // Parse date without timezone conversion (YYYY-MM-DD format)
          const [year, month, day] = entry.date.split('-').map(Number);
          const localDate = new Date(year, month - 1, day);
          
          return {
            date: localDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            fullDate: entry.date,
            weight: entry.weight,
            unit: entry.unit,
            notes: entry.notes
          };
        });
        
        setChartData(transformed);
        setEntries(response.data.data);
      } else {
        setChartData([]);
        setEntries([]);
      }
    } catch (error) {
      console.error('Error fetching weight history:', error);
      setChartData([]);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!weight || !date) {
      toast.error('Please enter both date and weight');
      return;
    }

    try {
      setSaving(true);
      const response = await axios.post(`${API}/users/${user.id}/weight-tracker`, {
        date,
        weight: parseFloat(weight),
        unit,
        notes
      });

      if (response.data.success) {
        toast.success('Weight entry saved!');
        // Reset form
        setWeight('');
        setNotes('');
        setDate(getLocalDate());
        // Refresh data
        fetchWeightHistory();
      }
    } catch (error) {
      console.error('Error saving weight:', error);
      toast.error('Error saving weight entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryDate) => {
    if (!window.confirm('Delete this weight entry?')) return;

    try {
      const response = await axios.delete(`${API}/users/${user.id}/weight-tracker/${entryDate}`);
      if (response.data.success) {
        toast.success('Weight entry deleted');
        fetchWeightHistory();
      }
    } catch (error) {
      console.error('Error deleting weight:', error);
      toast.error('Error deleting entry');
    }
  };

  const calculateStats = () => {
    if (entries.length === 0) return null;

    const weights = entries.map(e => e.weight);
    const startWeight = weights[0];
    const currentWeight = weights[weights.length - 1];
    const change = parseFloat((currentWeight - startWeight).toFixed(2));
    const percentChange = ((change / startWeight) * 100).toFixed(1);
    const avgWeight = (weights.reduce((a, b) => a + b, 0) / weights.length).toFixed(1);
    const minWeight = Math.min(...weights).toFixed(1);
    const maxWeight = Math.max(...weights).toFixed(1);

    return { startWeight, currentWeight, change, percentChange, avgWeight, minWeight, maxWeight };
  };

  const stats = calculateStats();

  return (
    <div className="space-y-6">
      <Card className="bg-white border-burgundy/20">
        <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-cream">
          <CardTitle className="font-serif">Weight Tracker</CardTitle>
          <CardDescription className="text-cream/90">Track your weight to see your progress over time</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-6">
          {/* Input Form */}
          <div className="bg-burgundy/5 rounded-lg p-4 space-y-4">
            <h3 className="font-semibold text-burgundy mb-3">Log Weight Entry</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Date</label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="border-burgundy/30 focus:border-gold"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Weight</label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="Enter weight"
                    className="border-burgundy/30 focus:border-gold"
                  />
                  <select
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    className="border border-burgundy/30 rounded-md px-3 focus:border-gold focus:outline-none"
                  >
                    <option value="lbs">lbs</option>
                    <option value="kg">kg</option>
                  </select>
                </div>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-burgundy hover:bg-burgundy/80 text-white w-full"
                >
                  {saving ? 'Saving...' : 'Save Entry'}
                </Button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Notes (optional)</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this entry..."
                className="border-burgundy/30 focus:border-gold"
                rows={2}
              />
            </div>
          </div>

          {/* Stats Summary */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-4 border border-burgundy/20">
                <p className="text-xs text-burgundy/60 mb-1">Current</p>
                <p className="text-xl font-bold text-burgundy">{stats.currentWeight} {unit}</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-burgundy/20">
                <p className="text-xs text-burgundy/60 mb-1">Change</p>
                <p className={`text-xl font-bold flex items-center ${stats.change > 0 ? 'text-orange-600' : stats.change < 0 ? 'text-green-600' : 'text-burgundy'}`}>
                  {stats.change > 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : stats.change < 0 ? <TrendingDown className="h-4 w-4 mr-1" /> : null}
                  {stats.change > 0 ? '+' : ''}{stats.change} {unit}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-burgundy/20">
                <p className="text-xs text-burgundy/60 mb-1">Average</p>
                <p className="text-xl font-bold text-burgundy">{stats.avgWeight} {unit}</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-burgundy/20">
                <p className="text-xs text-burgundy/60 mb-1">Range</p>
                <p className="text-sm font-bold text-burgundy">{stats.minWeight} - {stats.maxWeight} {unit}</p>
              </div>
            </div>
          )}

          {/* Time Period Filters */}
          <div className="flex flex-wrap gap-2">
            {[15, 30, 45, 60, 75, 90].map(days => (
              <Button
                key={days}
                onClick={() => setSelectedPeriod(days)}
                variant={selectedPeriod === days ? 'default' : 'outline'}
                className={selectedPeriod === days 
                  ? 'bg-burgundy hover:bg-burgundy/80 text-white' 
                  : 'border-burgundy text-burgundy hover:bg-burgundy hover:text-white'
                }
                size="sm"
              >
                {days} Days
              </Button>
            ))}
          </div>

          {/* Chart */}
          {loading ? (
            <div className="animate-pulse bg-burgundy/5 rounded-lg h-64"></div>
          ) : chartData.length > 0 ? (
            <div className="bg-white rounded-lg p-4 border border-burgundy/20">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#7C2D1210" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#7C2D12"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis 
                    stroke="#7C2D12"
                    style={{ fontSize: '12px' }}
                    domain={['dataMin - 2', 'dataMax + 2']}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#FFF8F0', 
                      border: '1px solid #7C2D1230',
                      borderRadius: '8px'
                    }}
                  />
                  {stats && (
                    <ReferenceLine 
                      y={parseFloat(stats.avgWeight)} 
                      stroke="#7C2D12" 
                      strokeDasharray="3 3"
                      label={{ value: 'Average', position: 'right', fill: '#7C2D12', fontSize: 12 }}
                    />
                  )}
                  <Line
                    type="monotone"
                    dataKey="weight"
                    name="Weight"
                    stroke="#7C2D12"
                    strokeWidth={3}
                    dot={{ r: 4, fill: '#7C2D12' }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center py-12 bg-burgundy/5 rounded-lg">
              <Scale className="h-16 w-16 text-burgundy/30 mx-auto mb-4" />
              <p className="text-burgundy/60 mb-2">No weight entries yet</p>
              <p className="text-xs text-burgundy/50">Start tracking your weight to see your progress!</p>
            </div>
          )}

          {/* Entry History */}
          {entries.length > 0 && (
            <div>
              <h4 className="font-semibold text-burgundy mb-3">Recent Entries</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {entries.slice().reverse().map((entry) => (
                  <div key={entry.date} className="flex justify-between items-center bg-white rounded-lg p-3 border border-burgundy/10">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <p className="font-medium text-burgundy">
                          {(() => {
                            // Parse date without timezone conversion
                            const [year, month, day] = entry.date.split('-').map(Number);
                            const localDate = new Date(year, month - 1, day);
                            return localDate.toLocaleDateString('en-US', { 
                              weekday: 'short', 
                              month: 'short', 
                              day: 'numeric',
                              year: 'numeric'
                            });
                          })()}
                        </p>
                        <Badge variant="outline" className="border-burgundy/30 text-burgundy">
                          {entry.weight} {entry.unit}
                        </Badge>
                      </div>
                      {entry.notes && (
                        <p className="text-xs text-burgundy/60 mt-1">{entry.notes}</p>
                      )}
                    </div>
                    <Button
                      onClick={() => handleDelete(entry.date)}
                      variant="ghost"
                      size="sm"
                      className="text-burgundy/50 hover:text-burgundy hover:bg-burgundy/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WeightTracker;
